import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';
import { createTranslatedWallCopy, createRotatedWallCopy } from '../../utils/WallGeometryUtils';
import { transformElementCopy } from '../../utils/elementTransformUtils';

export class ArrayToolClass extends BaseTool {
    id = 'ARRAY';
    name = 'Array';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private count: number = 3; // Default array count
    private mode: 'Linear' | 'Radial' = 'Linear';

    constructor(mode: 'Linear' | 'Radial' = 'Linear') {
        super();
        this.mode = mode;
        if (mode === 'Radial') {
            this.id = 'ARRAY_RADIAL';
            this.name = 'Radial Array';
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const selection = this.context.appStore.selectedIds;
        if (selection.length === 0) {
            // Click-to-select: pick the element under the pointer first; the
            // next click places the array vector start point.
            const id = this.findElementIdAtPoint(event);
            if (id) this.selectElement(id);
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        if (!this.startPoint) {
            // First click: Start point of translation vector
            this.startPoint = snappedPoint;
            console.log('Array: Start point selected', this.startPoint);

            // Show preview line?
            this.context.appStore.setToolPreview({
                active: true,
                start: this.startPoint,
                current: this.startPoint,
                toolId: this.id
            });
        } else {
            // Second click: End point of translation vector
            const endPoint = snappedPoint;
            console.log('Array: End point selected', endPoint);

            if (this.mode === 'Linear') {
                this.createLinearArray(selection, this.startPoint, endPoint, this.count);
            } else {
                this.createRadialArray(selection, this.startPoint, endPoint, this.count);
            }

            // Reset
            this.startPoint = null;
            this.context.appStore.setToolPreview(null);
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        // Preview depends on mode
        this.context.appStore.setToolPreview({
            active: true,
            start: this.startPoint,
            current: snappedPoint,
            toolId: this.id,
            mode: this.mode
        });
    }

    onPointerUp(): void { }

    private createLinearArray(ids: string[], start: THREE.Vector3, end: THREE.Vector3, count: number) {
        const translation = end.clone().sub(start);

        // Create (count - 1) copies. Walls keep their dedicated translated
        // copy path (fresh render geometry); every other category goes
        // through the generic transform walker.
        for (let i = 1; i < count; i++) {
            const stepTranslation = translation.clone().multiplyScalar(i); // Linear array: 2nd item at 1x dist, 3rd at 2x dist...

            ids.forEach(id => {
                const element = this.context!.bimStore.project.elements.get(id);
                if (!element) return;

                if (element.category === 'Walls') {
                    // Fresh render geometry: a clone carrying the original's
                    // layer profiles would render at the original's location.
                    const newWall = createTranslatedWallCopy(element as WallElement, stepTranslation);
                    this.context!.bimStore.addElement(newWall);
                } else {
                    const copy = transformElementCopy(element, {
                        translate: [stepTranslation.x, stepTranslation.y, stepTranslation.z],
                    }, THREE.MathUtils.generateUUID());
                    this.context!.bimStore.addElement(copy as any);
                }
            });
        }
        this.notifyCopied(count - 1);
    }

    /**
     * Radial (polar) array: `center` is the rotation centre (first click);
     * `refPoint` is the second click — the angle from the selection's
     * reference direction to the click is the angle step between successive
     * copies. Every selected element is rotated about the centre per copy.
     */
    private createRadialArray(ids: string[], center: THREE.Vector3, refPoint: THREE.Vector3, count: number) {
        // Selection reference direction: centre of the selection's extents.
        const selected = ids
            .map((id) => this.context!.bimStore.project.elements.get(id))
            .filter(Boolean);
        if (selected.length === 0) return;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const el of selected) {
            const g = (el as any).geometryData ?? {};
            for (const key of ['start', 'end', 'location', 'position', 'origin']) {
                const p = g[key];
                if (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number') {
                    minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
                    minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
                }
            }
        }
        if (!isFinite(minX)) {
            this.notify('Selection carries no placeable geometry for a radial array.', 'error');
            return;
        }
        const refX = (minX + maxX) / 2 - center.x;
        const refY = (minY + maxY) / 2 - center.y;
        const clickX = refPoint.x - center.x;
        const clickY = refPoint.y - center.y;

        if (Math.hypot(clickX, clickY) < 1e-6 || Math.hypot(refX, refY) < 1e-6) {
            this.notify('Radial array needs the second click away from the centre point.', 'error');
            return;
        }

        const stepAngle = Math.atan2(clickX, clickY) - Math.atan2(refX, refY);
        if (Math.abs(stepAngle) < 1e-4) {
            this.notify('Radial array angle step is zero — pick a different second point.', 'warning');
            return;
        }

        for (let i = 1; i < count; i++) {
            const angle = stepAngle * i;
            for (const id of ids) {
                const element = this.context!.bimStore.project.elements.get(id);
                if (!element) return;

                if (element.category === 'Walls') {
                    // Fresh join geometry recomputed from the rotated run.
                    const copy = createRotatedWallCopy(element as WallElement, center, angle);
                    this.context!.bimStore.addElement(copy);
                } else {
                    const copy = transformElementCopy(element, {
                        rotate: { center, angleRad: angle },
                    }, THREE.MathUtils.generateUUID());
                    this.context!.bimStore.addElement(copy as any);
                }
            }
        }
        this.notifyCopied(count - 1);
    }

    /** Toast feedback with the produced copy count. */
    private notifyCopied(copies: number) {
        this.notify(copies === 1 ? 'Array: 1 copy created.' : `Array: ${copies} copies created.`, 'success');
    }

    deactivate() {
        super.deactivate();
        this.startPoint = null;
        this.context?.appStore.setToolPreview(null);
    }
}
